import { useCallback, useEffect, useState } from "react"
import { Button, Input, message, Modal, Tooltip, Checkbox } from "antd"
import { getObservations } from "../services/GeminiServices"
import { useAuth } from "../contexts/authContext"
import {
  addDiagnosis,
  addFindings,
  addNotes,
  addRequest,
  generateDynamicPDF,
  updateImages,
} from "../services/ReportService"
import { useTranslation } from "react-i18next"
import { Download, FlaskConical, Loader } from "lucide-react"
import "../assets/styles/InformeModal.css"

const { TextArea } = Input

/**
 * Modal component for previewing and generating reports.
 *
 * @param {Object} props - Component props
 * @param {boolean} props.isVisible - Whether the modal is visible
 * @param {Function} props.onClose - Function to close the modal
 * @param {string} props.diagnostico - The diagnosis text
 * @param {string} props.solicitud - The request text
 * @param {Array} props.observaciones - Array of observations
 * @param {Array} props.images - Array of images
 * @param {Function} props.updateRightColumnState - Function to update the parent state
 * @returns {JSX.Element} The InformeModal component
 */
const InformeModal = ({
  isVisible,
  onClose,
  diagnostico,
  solicitud,
  observaciones,
  images,
  updateRightColumnState,
}) => {
  const { t } = useTranslation()
  const [editedSolicitud, setEditedSolicitud] = useState(solicitud)
  const [editedObservaciones, setEditedObservaciones] = useState(observaciones.join("\n"))
  const [editedDiagnostico, setEditedDiagnostico] = useState(diagnostico)
  const [editedNotas, setEditedNotas] = useState("")
  const [isLoading, setIsLoading] = useState(false)
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false)
  const [diagnosticoSeparado, setDiagnosticoSeparado] = useState(false)

  const { getToken } = useAuth()

  /**
   * Updates the local state for a specific field.
   *
   * @param {string} field - The field to update
   * @param {any} value - The new value
   */
  const updateLocalState = useCallback((field, value) => {
    switch (field) {
      case "solicitud":
        setEditedSolicitud(value)
        break
      case "observaciones":
        setEditedObservaciones(value)
        break
      case "diagnostico":
        setEditedDiagnostico(value)
        break
      case "notas":
        setEditedNotas(value)
        break
      default:
        console.warn(`Unhandled field in updateLocalState: ${field}`)
    }
  }, [])

  useEffect(() => {
    updateLocalState("solicitud", solicitud)
    updateLocalState("observaciones", observaciones.join("\n"))
    updateLocalState("diagnostico", diagnostico)
  }, [solicitud, observaciones, diagnostico, updateLocalState])

  /**
   * Handles changes to form fields and updates both local and parent state.
   *
   * @param {string} field - The field to update
   * @param {any} value - The new value
   */
  const handleChange = useCallback(
    (field, value) => {
      updateLocalState(field, value)
      if (field === "observaciones") {
        updateRightColumnState(field, value.split("\n"))
      } else {
        updateRightColumnState(field, value)
      }
    },
    [updateLocalState, updateRightColumnState],
  )

  /**
   * Uses AI to generate observations based on the request and existing observations.
   */
  const handleMagicPencil = async () => {
    setIsLoading(true)
    try {
      const prompt = `${editedSolicitud}. ${editedObservaciones}`

      const token = await getToken()
      if (!token) {
        throw new Error("No authentication token found.")
      }

      const result = await getObservations(prompt, token)
      if (result && result.response) {
        const observations = result.response.replace("Observaciones:\n", "").trim()
        handleChange("observaciones", observations)
        message.success(t("informeModal.successObservations"))
      } else {
        message.error(t("informeModal.errorObservations"))
      }
    } catch (error) {
      console.error("Error al obtener observaciones:", error)
      message.error(t("informeModal.errorProcessing"))
    } finally {
      setIsLoading(false)
    }
  }

  /**
   * Generates a PDF report with the current data.
   */
  const handleGenerateDynamicPDF = async () => {
    setIsGeneratingPDF(true)
    try {
      const token = await getToken()
      if (!token) {
        throw new Error("No authentication token found.")
      }

      // Process notes into an array
      const notesArray = editedNotas.split("\n").filter((note) => note.trim() !== "")

      // Initiate all three requests concurrently
      const reportId = JSON.parse(sessionStorage.getItem("report")).id
      await updateImages(token, reportId)
      await addRequest(token, reportId)
      await addFindings(token, reportId)
      await addDiagnosis(token, reportId)
      await addNotes(token, reportId, notesArray)
      await generateDynamicPDF(token, diagnosticoSeparado)

      // Provide feedback to the user upon success
      message.success(t("informeModal.successReport"))
      onClose()
    } catch (error) {
      console.error("Error generating PDF:", error)
      message.error(t("informeModal.errorReport"))
    } finally {
      setIsGeneratingPDF(false)
    }
  }

  return (
    <Modal
      className="informe-modal vistaprevia-modal"
      title={t("informeModal.title")}
      open={isVisible}
      onCancel={isGeneratingPDF ? undefined : onClose}
      footer={
        <div className="informe-modal-footer">
          <Button key="cancel" onClick={onClose} disabled={isGeneratingPDF} className="redesigned-danger-button">
            {t("informeModal.close")}
          </Button>
          <div className="informe-modal-actions">
            <Checkbox
              checked={diagnosticoSeparado}
              onChange={(e) => setDiagnosticoSeparado(e.target.checked)}
              className="informe-modal-checkbox checkboxx"
            >
              {t("informeModal.separateDiagnosis")}
            </Checkbox>
            <Button
              key="download"
              className="vistaprevia-button generate-button2 informe-modal-button"
              onClick={handleGenerateDynamicPDF}
              loading={isGeneratingPDF}
              disabled={
                isGeneratingPDF || !editedSolicitud.trim() || !editedObservaciones.trim() || !editedDiagnostico.trim()
              }
              icon={
                isGeneratingPDF ? (
                  <Loader className="informe-modal-icon-spin" />
                ) : (
                  <Download className="informe-modal-icon" />
                )
              }
            >
              {isGeneratingPDF ? t("informeModal.generating") : t("informeModal.download")}
            </Button>
          </div>
        </div>
      }
      width={900}
    >
      <div className="informe-modal-section">
        <h3 className="informe-modal-section-title preview-title">{t("informeModal.request")}</h3>
        <TextArea
          value={editedSolicitud}
          onChange={(e) => handleChange("solicitud", e.target.value)}
          rows={3}
          className="informe-modal-textarea"
        />
      </div>
      <div className="informe-modal-section">
        <h3 className="informe-modal-section-title preview-title">{t("informeModal.observations")}</h3>
        <div className="informe-modal-textarea-container">
          <TextArea
            value={editedObservaciones}
            onChange={(e) => handleChange("observaciones", e.target.value)}
            rows={6}
            className="informe-modal-textarea"
          />
          <Tooltip title={t("informeModal.magicCompletion")}>
            <Button
              icon={
                isLoading ? (
                  <Loader className="informe-modal-icon-spin" />
                ) : (
                  <FlaskConical className="informe-modal-icon" />
                )
              }
              onClick={handleMagicPencil}
              disabled={isLoading}
              className="informe-modal-magic-btn guardar-button2"
              aria-label={t("informeModal.magicCompletion")}
            />
          </Tooltip>
        </div>
      </div>
      <div className="informe-modal-section">
        <h3 className="informe-modal-section-title preview-title">{t("informeModal.diagnosis")}</h3>
        <TextArea
          value={editedDiagnostico}
          onChange={(e) => handleChange("diagnostico", e.target.value)}
          rows={4}
          className="informe-modal-textarea"
        />
      </div>
      <div className="informe-modal-section">
        <h3 className="informe-modal-section-title preview-title">{t("informeModal.notes")}</h3>
        <TextArea
          value={editedNotas}
          onChange={(e) => handleChange("notas", e.target.value)}
          rows={3}
          placeholder={t("informeModal.notesPlaceholder")}
          className="informe-modal-textarea"
        />
      </div>
    </Modal>
  )
}

export default InformeModal
